import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { Link } from '@inertiajs/react';

const PrivacyPolicy = () => {
  return (
    <Band tone="raised">
      {/* Prose width, not max-w-5xl: legal text is read line by line, and a
          1024px measure is roughly twice what the eye tracks comfortably. */}
      <SectionLayout width="prose" space="lg">
        <Typography.H1>Privacy Policy</Typography.H1>
        <Typography.Small>Last updated: September 06, 2026</Typography.Small>
        <Typography.Body>
          This Privacy Policy describes Our policies and procedures on the
          collection, use and disclosure of Your information when You use the
          Service and tells You about Your privacy rights and how the law
          protects You.
        </Typography.Body>
        <Typography.Body>
          We use Your Personal data to provide and improve the Service. By using
          the Service, You agree to the collection and use of information in
          accordance with this Privacy Policy.
        </Typography.Body>
        <Typography.H2>Interpretation and Definitions</Typography.H2>
        <Typography.H3>Interpretation</Typography.H3>
        <Typography.Body>
          The words of which the initial letter is capitalized have meanings
          defined under the following conditions. The following definitions
          shall have the same meaning regardless of whether they appear in
          singular or in plural.
        </Typography.Body>
        <Typography.H3>Definitions</Typography.H3>
        <Typography.Body>
          For the purposes of this Privacy Policy:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>Account</strong> means a unique account created for You to
              access our Service or parts of our Service.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Affiliate</strong> means an entity that controls, is
              controlled by or is under common control with a party, where
              &quot;control&quot; means ownership of 50% or more of the shares,
              equity interest or other securities entitled to vote for election
              of directors or other managing authority.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Academy</strong> (referred to as either &quot;the
              Academy&quot;, &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot;
              in this Agreement) refers to Samuthrika Academy, 10th Cross West
              Extension, Thillai Nagar, Tiruchirappalli, Tamil Nadu - 620018.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Cookies</strong> are small files that are placed on Your
              computer, mobile device or any other device by a website,
              containing the details of Your browsing history on that website
              among its many uses.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Country</strong> refers to: Tamil Nadu, India
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Device</strong> means any device that can access the
              Service such as a computer, a cellphone or a digital tablet.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Personal Data</strong> is any information that relates to
              an identified or identifiable individual.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Service</strong> refers to the Website.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Service Provider</strong> means any natural or legal
              person who processes the data on behalf of the Academy. It refers
              to third-party companies or individuals employed by the Academy to
              facilitate the Service, to provide the Service on behalf of the
              Academy, to perform services related to the Service or to assist
              the Academy in analyzing how the Service is used.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Usage Data</strong> refers to data collected
              automatically, either generated by the use of the Service or from
              the Service infrastructure itself (for example, the duration of a
              page visit).
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Website</strong> refers to Samuthrika Academy, accessible
              from{' '}
              <Link
                href={route('home')}
                className="underline underline-offset-4 decoration-1"
              >
                samuthrika.academy
              </Link>
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>You</strong> means the individual accessing or using the
              Service, or the academy, or other legal entity on behalf of which
              such individual is accessing or using the Service, as applicable.
            </Typography.Body>
          </li>
        </ul>
        <Typography.H2>Collecting and Using Your Personal Data</Typography.H2>
        <Typography.H3>Types of Data Collected</Typography.H3>
        <Typography.H3>Personal Data</Typography.H3>
        <Typography.Body>
          While using Our Service, We may ask You to provide Us with certain
          personally identifiable information that can be used to contact or
          identify You. Personally identifiable information may include, but is
          not limited to:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>Email address</Typography.Body>
          </li>
          <li>
            <Typography.Body>First name and last name</Typography.Body>
          </li>
          <li>
            <Typography.Body>Phone number</Typography.Body>
          </li>
          <li>
            <Typography.Body>
              Address, State, Province, ZIP/Postal code, City
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>Usage Data</Typography.Body>
          </li>
        </ul>
        <Typography.H3>Usage Data</Typography.H3>
        <Typography.Body>
          Usage Data is collected automatically when using the Service.
        </Typography.Body>
        <Typography.Body>
          Usage Data may include information such as Your Device&apos;s Internet
          Protocol address (e.g. IP address), browser type, browser version, the
          pages of our Service that You visit, the time and date of Your visit,
          the time spent on those pages, unique device identifiers and other
          diagnostic data.
        </Typography.Body>
        <Typography.Body>
          When You access the Service by or through a mobile device, We may
          collect certain information automatically, including, but not limited
          to, the type of mobile device You use, Your mobile device unique ID,
          the IP address of Your mobile device, Your mobile operating system,
          the type of mobile Internet browser You use, unique device identifiers
          and other diagnostic data.
        </Typography.Body>
        <Typography.Body>
          We may also collect information that Your browser sends whenever You
          visit our Service or when You access the Service by or through a
          mobile device.
        </Typography.Body>
        <Typography.H3>Tracking Technologies and Cookies</Typography.H3>
        <Typography.Body>
          We use Cookies and similar tracking technologies to track the activity
          on Our Service and store certain information. Tracking technologies
          used are beacons, tags, and scripts to collect and track information
          and to improve and analyze Our Service. The technologies We use may
          include:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>Cookies or Browser Cookies.</strong> A cookie is a small
              file placed on Your Device. You can instruct Your browser to
              refuse all Cookies or to indicate when a Cookie is being sent.
              However, if You do not accept Cookies, You may not be able to use
              some parts of our Service. Unless you have adjusted Your browser
              setting so that it will refuse Cookies, our Service may use
              Cookies.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Web Beacons.</strong> Certain sections of our Service and
              our emails may contain small electronic files known as web beacons
              (also referred to as clear gifs, pixel tags, and single-pixel
              gifs) that permit the Academy, for example, to count users who
              have visited those pages or opened an email and for other related
              website statistics (for example, recording the popularity of a
              certain section and verifying system and server integrity).
            </Typography.Body>
          </li>
        </ul>
        <Typography.Body>
          Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies.
          Persistent Cookies remain on Your personal computer or mobile device
          when You go offline, while Session Cookies are deleted as soon as You
          close Your web browser. You can learn more about cookies on{' '}
          <a
            href="https://www.termsfeed.com/blog/cookies/#What_Are_Cookies"
            target="_blank"
            rel="noreferrer"
          >
            TermsFeed website
          </a>{' '}
          article.
        </Typography.Body>
        <Typography.Body>
          We use both Session and Persistent Cookies for the purposes set out
          below:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>Necessary / Essential Cookies</strong>
            </Typography.Body>
            <Typography.Body>Type: Session Cookies</Typography.Body>
            <Typography.Body>Administered by: Us</Typography.Body>
            <Typography.Body>
              Purpose: These Cookies are essential to provide You with services
              available through the Website and to enable You to use some of its
              features. They help to authenticate users and prevent fraudulent
              use of user accounts. Without these Cookies, the services that You
              have asked for cannot be provided, and We only use these Cookies
              to provide You with those services.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Cookies Policy / Notice Acceptance Cookies</strong>
            </Typography.Body>
            <Typography.Body>Type: Persistent Cookies</Typography.Body>
            <Typography.Body>Administered by: Us</Typography.Body>
            <Typography.Body>
              Purpose: These Cookies identify if users have accepted the use of
              cookies on the Website.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Functionality Cookies</strong>
            </Typography.Body>
            <Typography.Body>Type: Persistent Cookies</Typography.Body>
            <Typography.Body>Administered by: Us</Typography.Body>
            <Typography.Body>
              Purpose: These Cookies allow us to remember choices You make when
              You use the Website, such as remembering your login details or
              language preference. The purpose of these Cookies is to provide
              You with a more personal experience and to avoid You having to
              re-enter your preferences every time You use the Website.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Advertising Cookies</strong>
            </Typography.Body>
            <Typography.Body>Type: Persistent Cookies</Typography.Body>
            <Typography.Body>
              Administered by: Third-Parties (Meta, Google)
            </Typography.Body>
            <Typography.Body>
              Purpose: These Cookies are set by the Meta Pixel and Google Ads
              (gtag.js) to track Your visits across the Website and other sites,
              so that the Academy can measure the effectiveness of its
              advertising campaigns and show You relevant ads on those
              platforms. See <em>Third-Party Advertising and Analytics</em>{' '}
              below for details.
            </Typography.Body>
          </li>
        </ul>
        <Typography.Body>
          For more information about the cookies we use and your choices
          regarding cookies, please visit our Cookies Policy or the Cookies
          section of our Privacy Policy.
        </Typography.Body>
        <Typography.H3>Use of Your Personal Data</Typography.H3>
        <Typography.Body>
          The Academy may use Personal Data for the following purposes:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>To provide and maintain our Service</strong>, including to
              monitor the usage of our Service.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>To manage Your Account:</strong> to manage Your
              registration as a user of the Service. The Personal Data You
              provide can give You access to different functionalities of the
              Service that are available to You as a registered user.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>For the performance of a contract:</strong> the
              development, compliance and undertaking of the purchase contract
              for the products, items or services You have purchased or of any
              other contract with Us through the Service.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>To contact You:</strong> To contact You by email,
              telephone calls, SMS, or other equivalent forms of electronic
              communication, such as a mobile application&apos;s push
              notifications regarding updates or informative communications
              related to the functionalities, products or contracted services,
              including the security updates, when necessary or reasonable for
              their implementation.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>To provide You</strong> with news, special offers and
              general information about other goods, services and events which
              we offer that are similar to those that you have already purchased
              or enquired about unless You have opted not to receive such
              information.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>To manage Your requests:</strong> To attend and manage
              Your requests to Us.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>For business transfers:</strong> We may use Your
              information to evaluate or conduct a merger, divestiture,
              restructuring, reorganization, dissolution, or other sale or
              transfer of some or all of Our assets, whether as a going concern
              or as part of bankruptcy, liquidation, or similar proceeding, in
              which Personal Data held by Us about our Service users is among
              the assets transferred.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>For other purposes</strong>: We may use Your information
              for other purposes, such as data analysis, identifying usage
              trends, determining the effectiveness of our promotional campaigns
              and to evaluate and improve our Service, products, services,
              marketing and your experience.
            </Typography.Body>
          </li>
        </ul>
        <Typography.Body>
          We may share Your personal information in the following situations:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>With Service Providers:</strong> We may share Your
              personal information with Service Providers to monitor and analyze
              the use of our Service, to contact You.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>For business transfers:</strong> We may share or transfer
              Your personal information in connection with, or during
              negotiations of, any merger, sale of Academy assets, financing, or
              acquisition of all or a portion of Our business to another
              academy.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>With Affiliates:</strong> We may share Your information
              with Our affiliates, in which case we will require those
              affiliates to honor this Privacy Policy. Affiliates include Our
              parent academy and any other subsidiaries, joint venture partners
              or other companies that We control or that are under common
              control with Us.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>With business partners:</strong> We may share Your
              information with Our business partners to offer You certain
              products, services or promotions.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>With other users:</strong> when You share personal
              information or otherwise interact in the public areas with other
              users, such information may be viewed by all users and may be
              publicly distributed outside.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>With Your consent</strong>: We may disclose Your personal
              information for any other purpose with Your consent.
            </Typography.Body>
          </li>
        </ul>
        <Typography.H3>Third-Party Advertising and Analytics</Typography.H3>
        <Typography.Body>
          We use the following third-party tools to measure and improve the
          performance of our advertising and to understand how visitors use the
          Service:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              <strong>Meta Pixel and Meta Conversions API.</strong> We use the
              Meta (Facebook) Pixel, together with Meta&apos;s Conversions API,
              to measure the effectiveness of Our advertising on Meta&apos;s
              platforms and to build audiences for remarketing. This sends Meta
              information such as the pages You visit, Your IP address and
              browser user agent, and — where You have provided it to Us, for
              example while placing an order or contacting Us — a
              cryptographically hashed version of Your email address, phone
              number and name. Meta uses this information subject to its own{' '}
              <a
                href="https://www.facebook.com/privacy/policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-1"
              >
                Privacy Policy
              </a>
              . You can review or opt out of Meta&apos;s use of this data for
              advertising through Meta&apos;s{' '}
              <a
                href="https://www.facebook.com/ads/preferences"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-1"
              >
                Ad Preferences
              </a>{' '}
              settings.
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              <strong>Google Ads (gtag.js).</strong> We use Google&apos;s tag
              (gtag.js) to track conversions from Our Google Ads campaigns,
              which sends Google information about the pages You visit and the
              actions You take on the Service. Google uses this information
              subject to its own{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-1"
              >
                Privacy Policy
              </a>
              . You can review or opt out of Google&apos;s use of this data for
              ad personalization through Google&apos;s{' '}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-1"
              >
                Ads Settings
              </a>
              .
            </Typography.Body>
          </li>
        </ul>
        <Typography.Body>
          These tools may transfer information about You outside of Your
          country, as described under Transfer of Your Personal Data below.
        </Typography.Body>
        <Typography.H3>Retention of Your Personal Data</Typography.H3>
        <Typography.Body>
          The Academy will retain Your Personal Data only for as long as is
          necessary for the purposes set out in this Privacy Policy. We will
          retain and use Your Personal Data to the extent necessary to comply
          with our legal obligations (for example, if we are required to retain
          your data to comply with applicable laws), resolve disputes, and
          enforce our legal agreements and policies.
        </Typography.Body>
        <Typography.Body>
          The Academy will also retain Usage Data for internal analysis
          purposes. Usage Data is generally retained for a shorter period of
          time, except when this data is used to strengthen the security or to
          improve the functionality of Our Service, or We are legally obligated
          to retain this data for longer time periods.
        </Typography.Body>
        <Typography.H3>Transfer of Your Personal Data</Typography.H3>
        <Typography.Body>
          Your information, including Personal Data, is processed at the
          Academy&apos;s operating offices and in any other places where the
          parties involved in the processing are located. It means that this
          information may be transferred to — and maintained on — computers
          located outside of Your state, province, country or other governmental
          jurisdiction where the data protection laws may differ than those from
          Your jurisdiction.
        </Typography.Body>
        <Typography.Body>
          Your consent to this Privacy Policy followed by Your submission of
          such information represents Your agreement to that transfer.
        </Typography.Body>
        <Typography.Body>
          The Academy will take all steps reasonably necessary to ensure that
          Your data is treated securely and in accordance with this Privacy
          Policy and no transfer of Your Personal Data will take place to an
          organization or a country unless there are adequate controls in place
          including the security of Your data and other personal information.
        </Typography.Body>
        <Typography.H3>Delete Your Personal Data</Typography.H3>
        <Typography.Body>
          You have the right to delete or request that We assist in deleting the
          Personal Data that We have collected about You.
        </Typography.Body>
        <Typography.Body>
          Our Service may give You the ability to delete certain information
          about You from within the Service.
        </Typography.Body>
        <Typography.Body>
          You may update, amend, or delete Your information at any time by
          signing in to Your Account, if you have one, and visiting the account
          settings section that allows you to manage Your personal information.
          You may also contact Us to request access to, correct, or delete any
          personal information that You have provided to Us.
        </Typography.Body>
        <Typography.Body>
          Please note, however, that We may need to retain certain information
          when we have a legal obligation or lawful basis to do so.
        </Typography.Body>
        <Typography.H3>Disclosure of Your Personal Data</Typography.H3>
        <Typography.H3>Business Transactions</Typography.H3>
        <Typography.Body>
          If the Academy is involved in a merger, acquisition or asset sale,
          Your Personal Data may be transferred. We will provide notice before
          Your Personal Data is transferred and becomes subject to a different
          Privacy Policy.
        </Typography.Body>
        <Typography.H3>Law enforcement</Typography.H3>
        <Typography.Body>
          Under certain circumstances, the Academy may be required to disclose
          Your Personal Data if required to do so by law or in response to valid
          requests by public authorities (e.g. a court or a government agency).
        </Typography.Body>
        <Typography.H3>Other legal requirements</Typography.H3>
        <Typography.Body>
          The Academy may disclose Your Personal Data in the good faith belief
          that such action is necessary to:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>Comply with a legal obligation</Typography.Body>
          </li>
          <li>
            <Typography.Body>
              Protect and defend the rights or property of the Academy
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              Prevent or investigate possible wrongdoing in connection with the
              Service
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>
              Protect the personal safety of Users of the Service or the public
            </Typography.Body>
          </li>
          <li>
            <Typography.Body>Protect against legal liability</Typography.Body>
          </li>
        </ul>
        <Typography.H3>Security of Your Personal Data</Typography.H3>
        <Typography.Body>
          The security of Your Personal Data is important to Us, but remember
          that no method of transmission over the Internet, or method of
          electronic storage is 100% secure. While We strive to use commercially
          acceptable means to protect Your Personal Data, We cannot guarantee
          its absolute security.
        </Typography.Body>
        <Typography.H2>Children&apos;s Privacy</Typography.H2>
        <Typography.Body>
          The Academy provides courses and services to individuals under the age
          of 18. Under the (Indian) Digital Personal Data Protection Act, 2023,
          anyone under 18 is a child, and We only collect and process a
          child&apos;s Personal Data — for example, information provided when
          enrolling in a course — with verifiable consent from the child&apos;s
          parent or lawful guardian, collected at the time of enrollment or
          registration rather than from the child directly.
        </Typography.Body>
        <Typography.Body>
          If You are a parent or guardian and believe We hold Your child&apos;s
          Personal Data without Your consent, please contact Us and We will take
          steps to verify and, if necessary, remove that information from Our
          servers.
        </Typography.Body>
        <Typography.H2>Links to Other Websites</Typography.H2>
        <Typography.Body>
          Our Service may contain links to other websites that are not operated
          by Us. If You click on a third party link, You will be directed to
          that third party&apos;s site. We strongly advise You to review the
          Privacy Policy of every site You visit.
        </Typography.Body>
        <Typography.Body>
          We have no control over and assume no responsibility for the content,
          privacy policies or practices of any third party sites or services.
        </Typography.Body>
        <Typography.H2>Changes to this Privacy Policy</Typography.H2>
        <Typography.Body>
          We may update Our Privacy Policy from time to time. We will notify You
          of any changes by posting the new Privacy Policy on this page.
        </Typography.Body>
        <Typography.Body>
          We will let You know via email and/or a prominent notice on Our
          Service, prior to the change becoming effective and update the
          &quot;Last updated&quot; date at the top of this Privacy Policy.
        </Typography.Body>
        <Typography.Body>
          You are advised to review this Privacy Policy periodically for any
          changes. Changes to this Privacy Policy are effective when they are
          posted on this page.
        </Typography.Body>
        <Typography.H2>Contact Us</Typography.H2>
        <Typography.Body>
          If you have any questions about this Privacy Policy, You can contact
          us:
        </Typography.Body>
        <ul className="list-disc space-y-3 pl-5 marker:text-thread">
          <li>
            <Typography.Body>
              By visiting this page on our website:{' '}
              <Link
                href={route('contact')}
                className="underline underline-offset-4 decoration-1"
              >
                samuthrika.academy/contact
              </Link>
            </Typography.Body>
          </li>
        </ul>
      </SectionLayout>
    </Band>
  );
};

export default PrivacyPolicy;
